// Netlify Blobs Storage Service for Kit Orders
// Works from both browser and serverless functions
import { getStore } from '@netlify/blobs'
import type { KitOrder } from './kitOrderService'

const STORE_NAME = 'kit-orders'
const ORDERS_KEY = 'all-orders'

class BlobOrderStorage {
  private store: ReturnType<typeof getStore> | null = null

  /**
   * Initialize the blob store
   */
  private getStoreInstance() {
    if (!this.store) {
      this.store = getStore({
        name: STORE_NAME,
        siteID: import.meta.env.VITE_NETLIFY_SITE_ID || process.env.SITE_ID,
        token: import.meta.env.VITE_NETLIFY_ACCESS_TOKEN || process.env.NETLIFY_TOKEN
      })
    }
    return this.store
  }

  /**
   * Get all orders from blob storage
   */
  async getOrders(): Promise<KitOrder[]> {
    try {
      const store = this.getStoreInstance()
      const data = await store.get(ORDERS_KEY)

      if (!data) {
        console.log('📦 No orders found in blob storage, returning empty array')
        return []
      }

      const orders = JSON.parse(data)
      console.log(`📦 Loaded ${orders.length} orders from blob storage`)
      return orders
    } catch (error) {
      console.error('❌ Error loading orders from blob storage:', error)
      return []
    }
  }

  /**
   * Save all orders to blob storage
   */
  async saveOrders(orders: KitOrder[]): Promise<void> {
    try {
      const store = this.getStoreInstance()
      await store.set(ORDERS_KEY, JSON.stringify(orders))
      console.log(`✅ Saved ${orders.length} orders to blob storage`)
    } catch (error) {
      console.error('❌ Error saving orders to blob storage:', error)
      throw error
    }
  }

  /**
   * Add a new order
   */
  async addOrder(order: KitOrder): Promise<void> {
    const orders = await this.getOrders()
    orders.push(order)
    await this.saveOrders(orders)
  }

  /**
   * Update an existing order
   */
  async updateOrder(orderId: string, updates: Partial<KitOrder>): Promise<KitOrder | null> {
    const orders = await this.getOrders()
    const orderIndex = orders.findIndex(o => o.id === orderId)

    if (orderIndex === -1) {
      console.error(`❌ Order not found: ${orderId}`)
      return null
    }

    orders[orderIndex] = {
      ...orders[orderIndex],
      ...updates,
      lastUpdated: new Date().toISOString()
    }

    await this.saveOrders(orders)
    console.log(`✅ Updated order: ${orderId}`)
    return orders[orderIndex]
  }

  /**
   * Delete an order
   */
  async deleteOrder(orderId: string): Promise<void> {
    const orders = await this.getOrders()
    const filteredOrders = orders.filter(o => o.id !== orderId)
    await this.saveOrders(filteredOrders)
    console.log(`✅ Deleted order: ${orderId}`)
  }

  /**
   * Get a single order by ID
   */
  async getOrderById(orderId: string): Promise<KitOrder | null> {
    const orders = await this.getOrders()
    return orders.find(o => o.id === orderId) || null
  }
}

// Export singleton instance
export const blobOrderStorage = new BlobOrderStorage()
